// src/components/ws/WsExportDialog.jsx
// 出席記録（全画面）の「書き出す」ダイアログ【2026-10-13】
//
//   もとは画面の右に設定欄がいつも出ていた（期間・形・書き方・見出し・追加シート・名簿を置かない運用…）。
//   書き出すときにしか使わないので、ボタンを押したときのダイアログにまとめた。
//   選ぶ順は 中身 → 期間 → 一緒に入れる表 → 形式。
//   出欠の書き方・回の見出しは出欠表タブと同じ設定（このブラウザに覚える）。
//   API はもとの書き出し（ReportExportPane）と同じ。
import React, { useEffect, useMemo, useState } from 'react';
import { MARKS_KEY, HEADING_KEY, SHEETS_KEY, loadMarks, loadHeading, readPref, writePref } from './wsCommon';

const EXTRA_SHEETS = [
  { key: 'trend',        label: '回ごとの推移',     group: 'att' },
  { key: 'risk',         label: '要注意の学生',     group: 'att' },
  { key: 'detail',       label: '打刻明細',         group: 'att' },
  { key: 'unregistered', label: 'アカウント未作成', group: 'att' },
  { key: 'audit',        label: '変更履歴',         group: 'att' },
  { key: 'quiz',         label: '小テスト',         group: 'manaba', singleOnly: true },
  { key: 'reflect',      label: '感想・発言',       group: 'manaba', singleOnly: true },
  { key: 'submission',   label: '提出物',           group: 'manaba', singleOnly: true },
  { key: 'fraud',        label: '不正疑い',         group: 'manaba' },
  { key: 'grade',        label: '成績集計',         group: 'manaba' },
];

const ymd = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
};
/** 前期 4/1〜9/30、後期 10/1〜翌3/31（教務の学期設定が無い場合の目安。もとの書き出しと同じ） */
function termRange(base, which) {
  const y = base.getFullYear();
  const m = base.getMonth() + 1;
  const inSpring = m >= 4 && m <= 9;
  let spring = inSpring;
  let year = y;
  if (which === 'previous') {
    spring = !inSpring;
    if (inSpring) year = y - 1;
    else if (m <= 3) year = y - 1;
  } else if (!inSpring && m <= 3) {
    year = y - 1;
  }
  return spring ? { start: year + '-04-01', end: year + '-09-30' } : { start: year + '-10-01', end: (year + 1) + '-03-31' };
}
const PRESETS = [
  { key: 'all',  label: '全期間',   range: () => ({ start: '', end: '' }) },
  { key: 'term', label: '今学期',   range: () => termRange(new Date(), 'current') },
  { key: 'prev', label: '前の学期', range: () => termRange(new Date(), 'previous') },
  { key: 'month', label: '今月', range: () => { const n = new Date(); return { start: ymd(new Date(n.getFullYear(), n.getMonth(), 1)), end: ymd(new Date(n.getFullYear(), n.getMonth() + 1, 0)) }; } },
  { key: 'lastmonth', label: '先月', range: () => { const n = new Date(); return { start: ymd(new Date(n.getFullYear(), n.getMonth() - 1, 1)), end: ymd(new Date(n.getFullYear(), n.getMonth(), 0)) }; } },
  { key: 'd30', label: '直近30日', range: () => { const n = new Date(); const s = new Date(n); s.setDate(s.getDate() - 29); return { start: ymd(s), end: ymd(n) }; } },
];

function loadSheets() {
  try {
    const v = JSON.parse(readPref(SHEETS_KEY, 'null'));
    if (Array.isArray(v)) return v.filter(k => EXTRA_SHEETS.some(s => s.key === k));
  } catch (e) { /* 既定に戻す */ }
  return ['trend', 'risk'];
}

export default function WsExportDialog({ open, onClose, classId, isAll, className, periodQuery = '', setErr, setMsg }) {
  const [kind, setKind]     = useState('register');   // register | summary | log
  const [start, setStart]   = useState('');
  const [end, setEnd]       = useState('');
  const [custom, setCustom] = useState(false);
  const [sheets, setSheetsS] = useState(loadSheets);
  const [fmt, setFmt]       = useState('xlsx');
  const [marks, setMarksS]  = useState(loadMarks);
  const [heading, setHeadingS] = useState(loadHeading);
  const [busy, setBusy]     = useState(false);

  // 開くたびに、出欠表タブで変えた書き方・見出しを読み直す
  useEffect(() => {
    if (!open) return undefined;
    setMarksS(loadMarks()); setHeadingS(loadHeading());
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const setSheets = (next) => { setSheetsS(next); writePref(SHEETS_KEY, JSON.stringify(next)); };
  const toggleSheet = (k) => setSheets(sheets.includes(k) ? sheets.filter(x => x !== k) : [...sheets, k]);
  const setMarks = (v) => { setMarksS(v); writePref(MARKS_KEY, v); };
  const setHeading = (v) => { setHeadingS(v); writePref(HEADING_KEY, v); };

  const kinds = isAll
    ? [{ key: 'register', label: '授業の一覧と出席簿', desc: '1枚目に授業を1行ずつ並べた一覧、次に授業ごとの出席簿' },
       { key: 'log', label: '出席ログ（氏名なし）', desc: '名簿を置かない運用。変換ツールで手元の名簿と合わせる' }]
    : [{ key: 'register', label: '出席簿', desc: '学生×回の出欠表。総出席日数つき' },
       { key: 'summary', label: '集計だけ', desc: '回の列なし。学生ごとの回数と出席率' },
       { key: 'log', label: '出席ログ（氏名なし）', desc: '名簿を置かない運用。変換ツールで手元の名簿と合わせる' }];
  const fmts = kind === 'log' ? [{ key: 'csv', label: 'CSV' }]
    : [{ key: 'xlsx', label: 'Excel' }, { key: 'csv', label: 'CSV' }, ...(isAll ? [] : [{ key: 'pdf', label: 'PDF' }])];
  const curFmt = fmts.some(f => f.key === fmt) ? fmt : fmts[0].key;
  const extra = EXTRA_SHEETS.filter(s => !s.singleOnly || !isAll);
  const showSheets = kind !== 'log' && curFmt === 'xlsx';
  const presetOn = PRESETS.find(p => { const r = p.range(); return !custom && r.start === start && r.end === end; });

  const fileName = useMemo(() => {
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const safe = String(className || classId).replace(/[\\/:*?"<>|]/g, '_');
    if (kind === 'log') return '出席ログ_' + (isAll ? '全授業' : safe) + '_' + stamp + '.csv';
    if (isAll) return '出席簿_集約_' + stamp + '.' + curFmt;
    if (curFmt === 'pdf') return 'レポート_' + safe + '_' + stamp + '.pdf';
    return (kind === 'summary' ? '出席集計_' : '出席簿_') + safe + '_' + stamp + '.' + curFmt;
  }, [kind, isAll, curFmt, className, classId]);

  const sheetCount = showSheets
    ? (kind === 'summary' ? 1 : 2) + sheets.filter(k => extra.some(s => s.key === k)).length
    : 1;

  const download = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const p = new URLSearchParams();
      if (start) p.append('startDate', start);
      if (end) p.append('endDate', end);
      if (!isAll && kind === 'summary') p.append('format', 'summary');
      const useSheets = sheets.filter(k => extra.some(s => s.key === k));
      if (useSheets.length) p.append('sheets', useSheets.join(','));
      if (marks === 'symbol') p.append('marks', 'symbol');
      if (heading === 'number') p.append('heading', 'number');
      if (isAll && periodQuery) new URLSearchParams(periodQuery).forEach((v, k) => p.append(k, v));
      const path = kind === 'log' ? 'log' : curFmt;
      const url = (isAll ? '/api/reports/all/' + path : '/api/reports/class/' + classId + '/' + path)
        + (p.toString() ? '?' + p.toString() : '');
      const res = await fetch(url, { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });
      if (!res.ok) {
        const text = await res.text();
        let m = text.slice(0, 200);
        try { const j = JSON.parse(text); m = j.message || j.error || m; } catch (e) { /* 文字のまま */ }
        throw new Error('書き出せませんでした（' + res.status + '）' + m);
      }
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl; a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
      setMsg('ダウンロードを開始しました（' + fileName + '）');
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  if (!open) return null;
  const seg = (on) => 'ws-seg-btn' + (on ? ' on' : '');

  return (
    <div className="ws-modal-back" onMouseDown={() => !busy && onClose()}>
      <section className="ws-modal" role="dialog" aria-modal="true" aria-labelledby="ws-ex-title"
               onMouseDown={e => e.stopPropagation()}>
        <div className="ws-modal-head">
          <h2 id="ws-ex-title">{isAll ? 'すべての授業を書き出す' : `${className || 'この授業'}を書き出す`}</h2>
          <button type="button" className="ws-icon-btn" aria-label="閉じる" onClick={onClose} disabled={busy}>×</button>
        </div>

        <div className="ws-modal-body">
          <fieldset>
            <legend>1. 中身</legend>
            <div className="ws-kind-grid">
              {kinds.map(k => (
                <button key={k.key} type="button" className={'ws-kind' + (kind === k.key ? ' on' : '')}
                        aria-pressed={kind === k.key} onClick={() => setKind(k.key)}>
                  <b>{k.label}</b><span>{k.desc}</span>
                </button>
              ))}
            </div>
            {kind === 'log' && (
              <div className="ws-muted" style={{ fontSize: '0.82rem', marginTop: '0.4rem' }}>
                打刻の事実（授業名・日付・学籍番号・状態）だけを書き出します。
                <a href="/attendance-converter.html" target="_blank" rel="noopener noreferrer" style={{ marginLeft: '0.3rem' }}>変換ツールを開く ↗</a>
              </div>
            )}
          </fieldset>

          <fieldset>
            <legend>2. 期間</legend>
            <div className="ws-chip-row">
              {PRESETS.map(ps => (
                <button key={ps.key} type="button" className={'ws-chip' + (presetOn && presetOn.key === ps.key ? ' on' : '')}
                        onClick={() => { const r = ps.range(); setStart(r.start); setEnd(r.end); setCustom(false); }}>
                  {ps.label}
                </button>
              ))}
              <button type="button" className={'ws-chip' + (custom || (!presetOn) ? ' on' : '')} onClick={() => setCustom(true)}>自分で指定</button>
            </div>
            {(custom || !presetOn) && (
              <div className="ws-chip-row" style={{ marginTop: '0.4rem' }}>
                <input type="date" value={start} onChange={e => setStart(e.target.value)} aria-label="開始日" />
                <span>〜</span>
                <input type="date" value={end} onChange={e => setEnd(e.target.value)} aria-label="終了日" />
              </div>
            )}
            <div className="ws-muted" style={{ fontSize: '0.82rem', marginTop: '0.3rem' }}>
              {start || end ? <>いま <b>{start || '最初'} 〜 {end || '最新'}</b> で絞っています</> : '全期間（絞っていません）'}
              {isAll && '。授業は、画面上の期間（年度・学期）の授業だけです'}
            </div>
          </fieldset>

          {kind !== 'log' && (
            <fieldset>
              <legend>3. 出欠の書き方</legend>
              <div className="ws-chip-row">
                <div className="ws-seg" role="group" aria-label="出欠の書き方">
                  <button type="button" className={seg(marks === 'text')} onClick={() => setMarks('text')}>文字（出席・遅刻…）</button>
                  <button type="button" className={seg(marks === 'symbol')} onClick={() => setMarks('symbol')}>記号（◯△▲◎×）</button>
                </div>
                <div className="ws-seg" role="group" aria-label="回の見出し">
                  <button type="button" className={seg(heading === 'date')} onClick={() => setHeading('date')}>見出しは日付</button>
                  <button type="button" className={seg(heading === 'number')} onClick={() => setHeading('number')}>第N回</button>
                </div>
              </div>
              <div className="ws-muted" style={{ fontSize: '0.78rem', marginTop: '0.3rem' }}>出欠表タブの表示と同じ設定です</div>
            </fieldset>
          )}

          <fieldset>
            <legend>{kind === 'log' ? '3' : '4'}. 形式</legend>
            <div className="ws-seg" role="group" aria-label="形式">
              {fmts.map(f => <button key={f.key} type="button" className={seg(curFmt === f.key)} onClick={() => setFmt(f.key)}>{f.label}</button>)}
            </div>
            <div className="ws-muted" style={{ fontSize: '0.8rem', marginTop: '0.3rem' }}>
              {kind === 'log' ? '出席ログはCSVだけです（UTF-8 BOM付き）'
                : curFmt === 'xlsx' ? '下で選んだ表がシートになります。罫線・色つき'
                : curFmt === 'csv' ? (isAll ? '授業の一覧の1枚だけです' : '1枚目（出席簿／集計）だけです') + '（UTF-8 BOM付き）'
                : '印刷・配布向けの固定レイアウトです。下の追加の表は入りません'}
            </div>
          </fieldset>

          {showSheets && (
            <fieldset>
              <legend>一緒に入れる表（Excel の別シート）</legend>
              <div className="ws-sheet-grid">
                <div className="ws-muted">出席</div>
                <div className="ws-muted">{isAll ? 'manaba（授業名の列を足して1枚に）' : 'manaba から取り込んだもの'}</div>
                {(() => {
                  const a = extra.filter(s => s.group === 'att');
                  const b = extra.filter(s => s.group === 'manaba');
                  const out = [];
                  for (let i = 0; i < Math.max(a.length, b.length); i++) {
                    [a[i], b[i]].forEach((s, j) => out.push(s ? (
                      <label key={s.key}><input type="checkbox" checked={sheets.includes(s.key)} onChange={() => toggleSheet(s.key)} />{s.label}</label>
                    ) : <span key={'e' + i + j} />));
                  }
                  return out;
                })()}
              </div>
            </fieldset>
          )}
        </div>

        <div className="ws-modal-foot">
          <span className="ws-muted" style={{ flex: 1, fontSize: '0.85rem', wordBreak: 'break-all' }}>
            {fileName}{curFmt === 'xlsx' && kind !== 'log' ? `・${sheetCount}シート${isAll ? '〜' : ''}` : ''}
          </span>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>キャンセル</button>
          <button type="button" className="btn-primary" onClick={download} disabled={busy}>{busy ? '書き出し中…' : '書き出す'}</button>
        </div>
      </section>
    </div>
  );
}
